/* eslint-disable react-hooks/exhaustive-deps */
/**
 * B010070 受注ワーク照会
 */
import { Column, Query } from "@material-table/core";
import { Box } from "@mui/material";
import {
  B000140AssistantNm,
  B000140ShainNm,
  B000150TokuiNm2,
  B010070Create,
  B010070CreateRequest,
  B010070Delete,
  B010070DeleteRequest,
  B010070Find,
  B010070FindRequest,
  B010070Get,
  B010070GetExcel,
  B010070GetExcelRequest,
  B010070GetRequest,
  catchFileError,
  catchRestError,
} from "clients/rest";
import {
  msgState,
  SCancelButton,
  SCodeSelect,
  SConfirmDialog,
  SDateField,
  SFileButton,
  SFrame,
  SLabel,
  SNormalButton,
  SNumberField,
  STable,
  SText,
  STextField,
} from "components";
import {
  B010070EdiKbn,
  B010070JuchuHaneiKbn,
  B010070StatusKbn,
  getSCode,
  SCode,
} from "messages/codes";
import { labels } from "messages/labels";
import { B000150, B010060InputData } from "pages";
import { B000140 } from "pages/b000/B000140";
import { createRef, useEffect, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";
import {
  atom,
  useRecoilState,
  useRecoilValue,
  useSetRecoilState,
} from "recoil";
import { focusState, navigateTypeState, useFocus } from "states";
import { dateWithSlashAt, download, mapIds } from "services";
import { getClientError, getClientMessage } from "messages";
import { editKbn, juchuHaneiKbn } from "services/codes";

export type B010070InputData = {
  ediKbn: string;
  juchustatusKbn: string;
};

// 呼び出し時のプロパティ
type Props = Partial<{
  ediKbn: string;
}>;

// コンポーネントで設定されるフィールドの型を定義する
type Fields = Partial<{
  ediKbn: SCode;
  juchuYmdFrom: string;
  juchuYmdTo: string;
  tokuiCd: string;
  tokuishitenCd: string;
  tokuiNm: string;
  shainCd: string;
  shainNm: string;
  assistantCd: string;
  assistantNm: string;
  statusKbn: SCode;
}>;

// テーブルデータ定義
type Data = Partial<{
  juchuworkdenpyoNo: string;
  gyoNo: string;
  juchuYmd: string;
  tokuiNm: string;
  meisaisu: string;
  setshohinKbn: string;
  juchuKin: string;
  juchuHaneiKbn: string;
  biko: string;
  version: string;
}>;

// フィールドの初期値を設定する。
const defaultFields: Fields = {
  ediKbn: getSCode(B010070EdiKbn, "1"),
  juchuYmdFrom: "",
  juchuYmdTo: "",
  tokuiCd: "",
  tokuishitenCd: "",
  tokuiNm: "",
  shainCd: "",
  shainNm: "",
  assistantCd: "",
  assistantNm: "",
  statusKbn: getSCode(B010070StatusKbn, "1"),
};

// テーブルを定義する。
const columns: Column<Data>[] = [
  { field: "juchuworkdenpyoNo", hidden: true },
  { field: "gyoNo", title: "行　", width: "2rem", align: "right" },
  {
    field: "juchuYmd",
    title: "受注日",
    width: "5rem",
    render: (data) => {
      if (data.juchuYmd! !== null && data.juchuYmd! !== "") {
        return dateWithSlashAt(data.juchuYmd!);
      }
    },
  },
  { field: "tokuiNm", title: "得意先", width: "10rem" },
  {
    field: "meisaisu",
    title: "明細数　",
    width: "2rem",
    align: "right",
    render: (data) => data.meisaisu?.toLocaleString(),
  },
  { field: "setshohinKbn", title: "セット構成品", width: "3.2rem" },
  {
    field: "juchuKin",
    title: "金額　",
    width: "3.5rem",
    align: "right",
    render: (data) => data.juchuKin?.toLocaleString(),
  },
  { field: "juchuHaneiKbn", hidden: true },
  {
    field: "juchuHaneiKbnView",
    title: "ステータス",
    width: "6rem",
    render: (data) => {
      return getSCode(
        B010070JuchuHaneiKbn,
        data.juchuHaneiKbn?.toString() ?? "0"
      )?.label;
    },
  },
  { field: "juchuHaneiDate", title: "受注反映日時", width: "6rem" },
  { field: "ediFileNm", title: "取込ファイル名", width: "9rem" },
  { field: "ediMemo", title: "EDIメモ", width: "7rem" },
];

// 検索条件を格納するデータタイプ
type SearchedFields = {
  searched: boolean;
} & Partial<{
  fields: Fields;
}>;

// 検索条件を格納するステート
export const B010070State = atom<SearchedFields>({
  key: "B010070State",
  default: {
    searched: false,
    fields: defaultFields,
  },
});

const focusId = "B010070";

// B010070 受注ワーク照会
export const B010070 = (props: Props) => {
  useFocus(focusId);
  return <Page {...props} />;
};

const Page = (props: Props) => {
  // 状態管理の初期設定
  // テーブルの値取得用Ref
  const tableRef = createRef();

  // フォーム関数
  const methods = useForm<Fields>({
    mode: "onBlur",
    defaultValues: defaultFields,
  });
  // 検索ボタン押下時に初期ページにするステート
  const isDefaultPage = useRef(false);
  // フォーカス設定
  const { focus, register } = useRecoilValue(focusState(focusId));

  // selectboxで選択された行のデータ
  const [selectedData, setSelectedData] = useState<Data[]>([]);

  const [searched, setSearched] = useState(false);

  // メッセージ表示
  const setMsg = useSetRecoilState(msgState);

  // ボタンの活性・非活性
  const [disabledDelete, setDisabledDelete] = useState(true);
  const [disabledReflectionCheck, setDisabledReflectionCheck] = useState(true);
  const [disabledDetail, setDisabledDetail] = useState(true);
  const [disabledReflect, setDisabledReflect] = useState(true);

  // 画面遷移の処理
  const navigate = useNavigate();
  const [menuNavigate, setMenuNavigate] = useRecoilState(navigateTypeState);

  // 遷移元データ取得用
  const location = useLocation();
  const inputData = location.state as B010070InputData;

  // 検索条件の保持
  const [searchedFeields, setSearchedFields] = useRecoilState(B010070State);

  // テーブル行で選択されたデータ
  const [rowData, setRowData] = useState<Partial<Data>>();

  // 初期表示イベント
  useEffect(() => {
    //メニューから遷移した場合
    if (menuNavigate === "menu") {
      focus(1);
      return;
    }
    //受注ワーク取込から遷移した場合
    else if (menuNavigate === "next") {
      //ediKbnに受注ワーク取込から受け取った値をセットする
      methods.setValue("ediKbn", getSCode(B010070EdiKbn, inputData.ediKbn!));
      methods.setValue(
        "statusKbn",
        getSCode(B010070StatusKbn, inputData.juchustatusKbn!)
      );
      //検索処理を実行する
      setSearched(true);
      return;
    }

    if (searchedFeields.searched) {
      // 検索条件が保存されている
      const fields = searchedFeields.fields;
      if (typeof fields !== "undefined") {
        methods.setValue("ediKbn", fields.ediKbn);
        methods.setValue("juchuYmdFrom", fields.juchuYmdFrom);
        methods.setValue("juchuYmdTo", fields.juchuYmdTo);
        methods.setValue("tokuiCd", fields.tokuiCd);
        methods.setValue("tokuishitenCd", fields.tokuishitenCd);
        methods.setValue("tokuiNm", fields.tokuiNm);
        methods.setValue("shainCd", fields.shainCd);
        methods.setValue("shainNm", fields.shainNm);
        methods.setValue("assistantCd", fields.assistantCd);
        methods.setValue("assistantNm", fields.assistantNm);
        methods.setValue("statusKbn", fields.statusKbn);
        setSearched(true);
        return;
      }
    }
  }, []);

  // 検索イベント
  const handleSearch = (query: Query<Data>) => {
    return new Promise<any>((resolve) => {
      // 初回検索をしない
      if (!searched) {
        resolve({
          data: [],
          page: 0,
          totalCount: 0,
        });
        return;
      }

      // 検索フォームのデータを取得する。
      const fields = methods.getValues();

      // 初期ページ表示の場合、0を設定する。
      query.page = isDefaultPage.current ? 0 : query.page;

      // 検索パラメータを作成する。
      const param: B010070FindRequest = {
        ediKbn: fields.ediKbn?.value!,
        juchustatusKbn: fields.statusKbn?.value!,
        juchuYmdFrom: fields.juchuYmdFrom,
        juchuYmdTo: fields.juchuYmdTo,
        tokuiCd: fields.tokuiCd,
        tokuishitenCd: fields.tokuishitenCd,
        shainCd: fields.shainCd,
        assistantCd: fields.assistantCd,
        // ページネーションの設定
        page: query.page,
        size: query.pageSize,
        // ソートの設定
        sort: ["juchuYmd,asc", "juchuworkdenpyoNo,asc"],
      };
      // API呼び出し
      B010070Find(param)
        // 正常取得時
        .then((value) => {
          const offset = query.pageSize * query.page;
          resolve({
            data: mapIds(value.data.content, "gyoNo", offset),
            page: value.data.number,
            totalCount: value.data.totalElements,
          });
        })
        // エラー時
        .catch((reason) => {
          catchRestError(reason, navigate, setMsg);
          resolve({
            data: [],
            page: 0,
            totalCount: 0,
          });
        })
        .finally(() => {
          // 初期ページ表示をオフにする。
          isDefaultPage.current = false;
        });
    });
  };

  // 検索時の初期化処理
  const handleResetSearch = () => {
    // ボタン非活性化
    setDisabledDelete(true);
    setDisabledReflectionCheck(true);
    setDisabledDetail(true);
    setDisabledReflect(true);
    // 選択されているデータを空にする
    setSelectedData([]);
    (tableRef.current as any).handleClearSelect();
  };

  // 検索ボタンクリックイベント
  const handleClickSearchButton = () => {
    // 初期化
    handleResetSearch();
    // 検索可能に変更
    setSearched(true);
    // 初期ページ設定
    isDefaultPage.current = true;
    // テーブル再検索
    (tableRef.current as any).onQueryChange();
  };

  // テーブル行チェックボックス選択時
  const handleCheckTableRow = (data: Data[]) => {
    // 行が1行も選択されていない場合
    if (data.length <= 0) {
      setDisabledDelete(true);
      setDisabledReflect(true);
      setSelectedData([]);
      return;
    }

    // 選択行の受注ステータスが「0(ファイル取込済)」、「1(構成品不備)」のみかチェックする
    const isDeletable = data.every((rows) => {
      return (
        rows.juchuHaneiKbn === juchuHaneiKbn.fileTorikomizumi ||
        rows.juchuHaneiKbn === juchuHaneiKbn.koseihinfubi
      );
    });
    // 選択行の受注ステータスが「0(ファイル取込済)」のみか判断する
    const isReflectable = data.every((rows) => {
      return rows.juchuHaneiKbn === juchuHaneiKbn.fileTorikomizumi;
    });

    // 削除可能な行が選択されている場合
    if (isDeletable) {
      setDisabledDelete(false);
    }
    // 削除可能な行以外が選択されている場合
    else {
      setDisabledDelete(true);
    }

    // 反映可能な行が選択されている場合
    if (isReflectable) {
      setDisabledReflect(false);
    }
    // 反映可能な行以外が選択されている場合
    else {
      setDisabledReflect(true);
    }

    setSelectedData(data);
  };

  // 削除ボタンクリックイベント
  const handleClickDeleteButton = () => {
    //リクエスト作成
    const param: B010070DeleteRequest = {
      b010070DeleteFieldList: selectedData.map((value) => {
        return {
          juchuworkdenphpyoNo: value.juchuworkdenpyoNo,
          version: value.version,
        };
      }),
    };
    //消去API呼び出し
    B010070Delete(param)
      .then(() => {
        // 削除後に先頭ページに戻る
        isDefaultPage.current = true;
        // テーブル再検索
        (tableRef.current as any).onQueryChange();
        // 各ボタン非活性化
        setDisabledDelete(true);
        setDisabledReflectionCheck(true);
        setDisabledDetail(true);
        setDisabledReflect(true);
        // 削除完了メッセージ設定
        setMsg(getClientMessage("B010070_001"));
      })
      .catch((reason) => {
        catchRestError(reason, navigate, setMsg);
      });
  };

  // テーブル行選択時
  const handleSelectTableRow = (_: any, rowData: Data | undefined) => {
    // 反確ボタンと詳細ボタンは行選択の時のみ活性
    // 反映確認ボタン（ファイル取込済・構成品不備の場合）の非活性
    if (
      rowData?.juchuHaneiKbn === juchuHaneiKbn.fileTorikomizumi ||
      rowData?.juchuHaneiKbn === juchuHaneiKbn.koseihinfubi
    ) {
      setDisabledDetail(false);
      setDisabledReflectionCheck(true);
    } else {
      setDisabledDetail(false);
      setDisabledReflectionCheck(false);
    }
    // データを保存
    setRowData(rowData);
  };

  // 反映結果確認ボタンクリックイベント
  const handleClickReflectionCheckButton = () => {
    // 行が選択されている場合のみ実行する。
    if (typeof rowData === "undefined") {
      return;
    }

    //リクエスト作成
    const param: B010070GetExcelRequest = {
      juchuworkdenpyoNo: rowData.juchuworkdenpyoNo,
    };
    //Excel出力API呼び出し
    B010070GetExcel(param)
      .then((response) => {
        download(response);
        setMsg(getClientMessage("B010070_003"));
      })
      .catch((reason) => {
        catchFileError(reason, navigate, setMsg);
      });
  };

  // テーブル行ダブルクリック時
  const handleDoubleClick = (data: Data) => {
    const param: B010070GetRequest = {
      juchuworkdenpyoNo: data.juchuworkdenpyoNo,
    };
    B010070Get(param)
      .then((value) => {
        // 反映済のデータ→参照モード、それ以外→編集モード
        const inputData: B010060InputData = {
          editKbn: editKbn.reference,
          juchuWork: value.data,
        };

        // 「2:受注反映済」、「3:受注反映済(一部在庫不足)」以外の場合、更新モードで開く
        if (
          data.juchuHaneiKbn !== juchuHaneiKbn.haneizumi &&
          data.juchuHaneiKbn !== juchuHaneiKbn.haneizumiZaikofusoku
        ) {
          inputData.editKbn = editKbn.update;
        }

        setSearchedFields({ searched: true, fields: methods.getValues() });
        // 受注ワーク入力画面へ遷移する。
        setMenuNavigate("next");
        navigate("/b010060", { state: inputData });
      })
      .catch((reason) => {
        catchRestError(reason, navigate, setMsg);
      });
  };

  // 詳細ボタンクリックイベント
  const handleClickDetailButton = () => {
    // 行が選択されている場合のみ実行する。
    if (typeof rowData === "undefined") {
      return;
    }

    B010070Get({ juchuworkdenpyoNo: rowData.juchuworkdenpyoNo })
      .then((value) => {
        // 反映済のデータ→参照モード、それ以外→編集モード
        const inputData: B010060InputData = {
          editKbn: editKbn.reference,
          juchuWork: value.data,
        };

        // 「2:受注反映済」、「3:受注反映済(一部在庫不足)」以外の場合、更新モードで開く
        if (
          rowData.juchuHaneiKbn !== juchuHaneiKbn.haneizumi &&
          rowData.juchuHaneiKbn !== juchuHaneiKbn.haneizumiZaikofusoku
        ) {
          inputData.editKbn = editKbn.update;
        }
        setSearchedFields({ searched: true, fields: methods.getValues() });
        // 受注ワーク入力画面へ遷移する。
        setMenuNavigate("next");
        navigate("/b010060", { state: inputData });
      })
      .catch((reason) => {
        catchRestError(reason, navigate, setMsg);
      });
  };

  // 反映ボタンクリックイベント
  const handleClickReflectButton = () => {
    const param: B010070CreateRequest = {
      b010070CreateFieldList: selectedData.map((value) => {
        return {
          juchuworkdenphpyoNo: value.juchuworkdenpyoNo,
          version: value.version,
        };
      }),
    };
    B010070Create(param)
      .then((value) => {
        // stateの変更後に、tableRefがNULLになるため、最初に再検索を行う
        //テーブル更新の為再検索
        (tableRef.current as any).onQueryChange();

        // stateの変更後に、Msgがクリアされるため先にstateを更新
        // ボタン非活性化
        setDisabledDelete(true);
        setDisabledReflectionCheck(true);
        setDisabledDetail(true);
        setDisabledReflect(true);
        // 選択されているデータを空にする
        setSelectedData([]);
        setRowData(undefined);

        // 在庫不足のレコードが無かった場合
        if (value.data.zaikofusokuGyoNoList.length === 0) {
          setMsg(getClientMessage("B010070_002"));
        }
        // 在庫不足のレコードがあった場合
        else {
          // 行番号を取得し、カンマ区切りの文字列に変換
          let errGyo = "";
          for (let i of value.data.zaikofusokuGyoNoList) {
            // 最初の1件
            if (errGyo === "") {
              errGyo = selectedData[i].gyoNo!.toString();
            }
            // 2件目以降
            else {
              errGyo += "," + selectedData[i].gyoNo!.toString();
            }
          }

          setMsg(getClientError("B010070_001", errGyo));
        }
      })
      .catch((reason) => {
        catchRestError(reason, navigate, setMsg);
      });
  };

  // レイアウト設定
  return (
    <FormProvider {...methods}>
      <SFrame
        title="受注ワーク照会"
        gridTemplateColumns="7rem 6rem 5rem 1rem 1rem 1rem 9rem 47rem"
      >
        <SLabel gridColumn={1}>取込データ種別</SLabel>
        <SCodeSelect
          name="ediKbn"
          gridColumn="span 2"
          options={B010070EdiKbn}
          {...register(1)}
        />
        <SLabel gridColumn={1}>受注日</SLabel>
        <SDateField name="juchuYmdFrom" gridColumn="span 2" {...register(2)} />
        <Box />
        <SText>～</SText>
        <Box />
        <SDateField name="juchuYmdTo" {...register(3)} />
        <SLabel gridColumn={1}>得意先</SLabel>
        <SNumberField
          name="tokuiCd"
          onBlur={() => B000150TokuiNm2(methods, navigate, setMsg)}
          code
          size={6}
          {...register(4)}
        />
        <SNumberField
          name="tokuishitenCd"
          code
          size={4}
          onBlur={() => B000150TokuiNm2(methods, navigate, setMsg)}
          dialog={<B000150 />}
          handleSubmit={(data: any) => {
            methods.setValue("tokuiCd", data.tokuiCd);
            methods.setValue("tokuishitenCd", data.tokuishitenCd);
            methods.setValue("tokuiNm", data.tokuiNmr);
          }}
          {...register(5)}
        />
        <Box />
        <STextField name="tokuiNm" gridColumn="span 3" size={40} disabled />
        <Box />
        <SLabel gridColumn={1}>受注担当者</SLabel>
        <SNumberField
          name="shainCd"
          code
          size={7}
          gridColumn="span 2"
          onBlur={() => B000140ShainNm(methods, navigate, setMsg)}
          {...register(6)}
          dialog={<B000140 />}
          handleSubmit={(data: any) => {
            methods.setValue("shainCd", data.shainCd);
            methods.setValue("shainNm", data.shainNm);
          }}
        />
        <Box />
        <STextField name="shainNm" gridColumn="span 3" size={40} disabled />
        <SLabel gridColumn={1}>アシスタント</SLabel>
        <SNumberField
          name="assistantCd"
          gridColumn="span 2"
          code
          size={7}
          onBlur={() => B000140AssistantNm(methods, navigate, setMsg)}
          {...register(7)}
          dialog={<B000140 />}
          handleSubmit={(data: any) => {
            methods.setValue("assistantCd", data.shainCd);
            methods.setValue("assistantNm", data.shainNm);
          }}
        />
        <Box />
        <STextField name="assistantNm" gridColumn="span 3" size={40} disabled />
        <SLabel gridColumn={1}>受注ステータス</SLabel>
        <SCodeSelect
          name="statusKbn"
          gridColumn="span 2"
          options={B010070StatusKbn}
          {...register(8)}
        />
        <SNormalButton
          label={labels.buttons.search}
          {...register(9)}
          onClick={handleClickSearchButton}
          gridRow="7"
          gridColumn={8}
          align="right"
        />
      </SFrame>
      <SFrame>
        <STable
          keyName="gyoNo"
          data={handleSearch}
          columns={columns}
          tableRef={tableRef}
          handleDoubleClick={handleDoubleClick}
          onRowClick={handleSelectTableRow}
          onSelectionChange={handleCheckTableRow}
          width={1280}
          height={340}
          multi
          onPageChange={handleResetSearch}
        />
      </SFrame>
      <SFrame gridTemplateColumns="3rem 4rem 30rem 6rem 23rem 4rem 3rem">
        <Box />
        <SCancelButton
          label={labels.buttons.delete}
          {...register(11)}
          disabled={disabledDelete}
          dialog={
            <SConfirmDialog
              title="受注ワーク入力"
              msg="選択中の受注ワークデータを削除します。よろしいですか？"
            />
          }
          dialogAction="confirm"
          handleSubmit={handleClickDeleteButton}
        />
        <Box />
        <SFileButton
          label={labels.buttons.reflectionCheck}
          {...register(12)}
          onClick={handleClickReflectionCheckButton}
          disabled={disabledReflectionCheck}
        />
        <Box />
        <SNormalButton
          label={labels.buttons.detail}
          {...register(13)}
          onClick={handleClickDetailButton}
          disabled={disabledDetail}
        />
        <SNormalButton
          label={labels.buttons.reflect}
          {...register(14)}
          disabled={disabledReflect}
          dialog={
            <SConfirmDialog
              title="受注ワーク入力"
              msg="選択中の受注ワークデータを受注データとして反映しますか？"
            />
          }
          dialogAction="confirm"
          handleSubmit={handleClickReflectButton}
        />
        <Box />
      </SFrame>
    </FormProvider>
  );
};
